import { ActionButton, OfficeButton, TableComponent } from 'components';
import { useAppLocation, useAppNavigate, useAppSearch } from '_graphql/cache/auth';

import CreateTreatyEndorsementContainer from './create';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { ModalActions } from 'utils/types';
import RemoveContainer from './remove';
import UpdateTreatyEndorsementContainer from './update';
import ViewTreatyEndorsementDocumentsContainer from './view';
import { useTreatyEndorsements } from 'hooks/data/use-treaties';

export default function TreatyEndorsementsTab() {
    const { params } = useAppLocation();
    const searchParams = useAppSearch()
    const navigate = useAppNavigate()
    const { loading, endorsements, refetch } = useTreatyEndorsements({
        filter: {
            treatiestreaty_id: params?.treaty || "",
        }
    });

    const handleAction = (id: string, idx: number, action: Exclude<ModalActions, "expand" | "goto" | "clone">) => () => {
        navigate({
            search: (old) => ({
                ...old,
                id,
                modal: action,
                number: idx.toString(),
            }),
        });
    };

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-3 sm:py-6 items-start px-3 flex-1">
                <div className='col-span-1 sm:col-span-3'>
                    <TableComponent
                        title={'endorsements'}
                        hasSearch
                        defaultView='grid'
                        viewType='grid'
                        loading={loading}
                        columns={[]}
                        renderGridItem={({ item, index }) => (
                            <div
                                className="bg-card-500 rounded-xl p-6 hover:bg-card-700/50 transition-colors"
                            >
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <DocumentTextIcon className="h-20 w-20 text-indigo-400" />
                                    </div>
                                    <div className="ml-4 flex-1">
                                        <h4 className="text-lg font-medium text-primary">Endorsement No. {index + 1}</h4>
                                        <p className="mt-1 text-sm text-tertiary">Created By: {item?.employee?.employee_first_name} {item?.employee?.employee_last_name}</p>
                                        <div className="mt-4 flex gap-3">
                                            <ActionButton
                                                action='view'
                                                onClick={handleAction(item.treaty_endorsement_id, index, "view-treaty-endorsement")}
                                            />
                                            <ActionButton
                                                action='update'
                                                onClick={handleAction(item.treaty_endorsement_id, index, "update-treaty-endorsement")}
                                            />
                                            <ActionButton
                                                action='remove'
                                                onClick={handleAction(item.treaty_endorsement_id, index, "remove-treaty-endorsement")}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        data={endorsements || []}
                        refetch={refetch}
                        renderActions={() => (
                            <OfficeButton size='sm' onClick={() => navigate({
                                search(prev) {
                                    return {
                                        ...prev,
                                        modal: "create-treaty-endorsement",
                                    }
                                },
                            })}>
                                <DocumentTextIcon className="h-6 w-6 " />
                                <span className="ml-2 hidden sm:inline">Create Endorsement</span>
                                <span className="ml-2 inline text-xs sm:hidden">Create</span>
                            </OfficeButton>
                        )}
                    />
                </div>
            </div>
            {searchParams?.modal && (
                <CreateTreatyEndorsementContainer
                    open={searchParams?.modal === "create-treaty-endorsement"}
                    setOpen={(val) => {
                        navigate({
                            search(prev) {
                                return {
                                    ...prev,
                                    modal: val ? "create-treaty-endorsement" : undefined,
                                }
                            },
                        });
                    }}
                    refetch={refetch}
                />
            )}

            {searchParams?.id && searchParams?.modal === "view-treaty-endorsement" && (
                <ViewTreatyEndorsementDocumentsContainer
                    open={searchParams?.modal === "view-treaty-endorsement"}
                    setOpen={(val) => {
                        navigate({
                            search(prev) {
                                return {
                                    ...prev,
                                    modal: val ? "view-treaty-endorsement" : undefined,
                                    id: val ? searchParams?.id : undefined,
                                }
                            },
                        });
                    }}
                />
            )}
            {searchParams?.id && searchParams?.modal === "update-treaty-endorsement" && (
                <UpdateTreatyEndorsementContainer
                    open={searchParams?.modal === "update-treaty-endorsement"}
                    setOpen={(val) => {
                        navigate({
                            search(prev) {
                                return {
                                    ...prev,
                                    modal: val ? "update-treaty-endorsement" : undefined,
                                    id: val ? searchParams?.id : undefined,
                                }
                            },
                        });
                    }}
                />
            )}
            {searchParams?.id && searchParams?.modal === "remove-treaty-endorsement" && (
                <RemoveContainer
                    open={searchParams?.modal === "remove-treaty-endorsement"}
                    setOpen={(val) => {
                        navigate({
                            search(prev) {
                                return {
                                    ...prev,
                                    modal: val ? "remove-treaty-endorsement" : undefined,
                                    id: val ? searchParams?.id : undefined,
                                }
                            },
                        });
                    }}
                />
            )}
        </>
    )
}
